/**
 * Orchestrator Configuration
 *
 * Reads AI configuration from GraphQL-backed settings cache.
 * No localStorage — all config comes from the server via useAiSettings.
 */

import type { CloudProvider, CloudClientConfig } from './cloudClient';
import { getAiSettingsFromCache } from '../../hooks/useAiSettings';

// =============================================================================
// CONFIG INTERFACE
// =============================================================================

export interface AIConfig {
  // Cloud AI config (for orchestration)
  cloud?: {
    provider: CloudProvider;
    apiKey: string;
    model?: string;
  };
  // Feature flag
  orchestratorEnabled: boolean;
}

// =============================================================================
// CONFIG FUNCTIONS
// =============================================================================

/**
 * Load AI configuration from GraphQL settings cache.
 * API keys are NOT stored client-side — the backend gateway injects them.
 */
export function loadAIConfig(): AIConfig {
  const settings = getAiSettingsFromCache();

  const provider = settings.provider;

  // Cloud providers route through the gateway (no API key needed client-side)
  if (provider === 'disabled') {
    return { orchestratorEnabled: false };
  }

  // For cloud providers (groq, openai, anthropic), the gateway holds the key.
  // We pass an empty apiKey — the gateway injects the real one.
  return {
    cloud: {
      provider: provider as CloudProvider,
      apiKey: 'gateway-managed',
      model: settings.chatModel || undefined,
    },
    orchestratorEnabled: true,
  };
}

/**
 * Save cloud config — now a no-op. Settings are saved via useSaveAiSettings hook.
 * @deprecated Use useSaveAiSettings() mutation instead
 */
export function saveCloudConfig(_provider: CloudProvider, _apiKey: string, _model?: string): void {
  console.warn('[AIConfig] saveCloudConfig is deprecated. Use useSaveAiSettings() instead.');
}

/**
 * Set orchestrator enabled — now a no-op.
 * @deprecated Use useSaveAiSettings() mutation instead
 */
export function setOrchestratorEnabled(_enabled: boolean): void {
  console.warn('[AIConfig] setOrchestratorEnabled is deprecated. Use useSaveAiSettings() instead.');
}

/**
 * Clear cloud config — now a no-op.
 * @deprecated Use useSaveAiSettings() mutation instead
 */
export function clearCloudConfig(): void {
  console.warn('[AIConfig] clearCloudConfig is deprecated. Use useSaveAiSettings() instead.');
}

/**
 * Check if the orchestrator can be used (cloud provider configured)
 */
export function isOrchestratorAvailable(): boolean {
  const config = loadAIConfig();
  return config.orchestratorEnabled && !!config.cloud;
}

/**
 * Convert AIConfig to CloudClientConfig for the CloudAIClient
 */
export function toCloudClientConfig(config: AIConfig): CloudClientConfig | null {
  if (!config.cloud) return null;
  return {
    provider: config.cloud.provider,
    apiKey: config.cloud.apiKey,
    model: config.cloud.model,
  };
}
